// PairRateMatrixPanel — buy/sell pair-rate matrix editor (spec §3.2).
//
// Rendered alongside CurrencyRatesPanel when namespace === 'currency-settings'.
// Rows = buy currencies, cols = sell currencies. Diagonal fixed at 1.0
// (degenerate identity, not stored). Blank cells = "no rate set" (prod
// hard-fails at lock; staging may flip allowRuntimeCrossFromEur=true).
//
// Editing model: each cell in-place edit → patches the full pairRates array
// via PUT /admin/configs/currency-settings (the existing namespace blob
// writer). Seed-from-EUR is the bootstrap action; it never overwrites manual
// entries.

import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { fetchAdminConfig, saveAdminConfig, seedPairRates } from '../lib/admin-api';

interface Cell {
  buy: string;
  sell: string;
  rate: number;
  updatedAt?: string;
  source?: 'manual' | 'cross_from_eur';
}

interface RawValue {
  displayCurrencies?: Array<{ code: string; rateToPrimary?: number }>;
  pairRates?: Cell[];
}

export function PairRateMatrixPanel() {
  const qc = useQueryClient();
  const cfg = useQuery({
    queryKey: ['admin', 'configs', 'currency-settings'],
    queryFn: ({ signal }) => fetchAdminConfig('currency-settings', signal),
  });
  const [editing, setEditing] = useState<{ buy: string; sell: string; value: string } | null>(null);
  const [seedResult, setSeedResult] = useState<string | null>(null);

  const value = (cfg.data?.value as RawValue | undefined) ?? {};
  const currencies = useMemo(() => (value.displayCurrencies ?? []).map((c) => c.code), [value]);
  const cellMap = useMemo(() => {
    const m = new Map<string, Cell>();
    for (const c of value.pairRates ?? []) {
      m.set(`${c.buy.toUpperCase()}_${c.sell.toUpperCase()}`, c);
    }
    return m;
  }, [value.pairRates]);

  const save = useMutation({
    mutationFn: async (next: Cell[]) => {
      const merged = { ...(value as Record<string, unknown>), pairRates: next };
      return saveAdminConfig('currency-settings', merged);
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['admin', 'configs', 'currency-settings'] }),
  });

  const seed = useMutation({
    mutationFn: () => seedPairRates(false),
    onSuccess: (r) => {
      setSeedResult(
        r.kind === 'applied' ? `Added ${r.added} cell${r.added === 1 ? '' : 's'} (total ${r.total}).`
        : r.kind === 'noop' ? 'All pairs already set.'
        : `Preview: would add ${r.additions.length}.`,
      );
      void qc.invalidateQueries({ queryKey: ['admin', 'configs', 'currency-settings'] });
    },
  });

  function commitCell(buy: string, sell: string, raw: string) {
    setEditing(null);
    const parsed = Number.parseFloat(raw);
    if (!Number.isFinite(parsed) || parsed <= 0) return;
    const key = `${buy.toUpperCase()}_${sell.toUpperCase()}`;
    const others = (value.pairRates ?? []).filter(
      (c) => `${c.buy.toUpperCase()}_${c.sell.toUpperCase()}` !== key,
    );
    const next: Cell[] = [
      ...others,
      { buy, sell, rate: parsed, updatedAt: new Date().toISOString(), source: 'manual' },
    ];
    save.mutate(next);
  }

  return (
    <section className="mt-6 rounded-xl border border-[color:var(--bo-line)] bg-white">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-[color:var(--bo-line)] px-5 py-3">
        <div>
          <div className="text-[13px] font-semibold text-[color:var(--bo-ink)]">
            Buy ↔ sell pair rates
          </div>
          <div className="mt-0.5 text-[11.5px] text-[color:var(--bo-mute)]">
            1 buy unit = N sell units. Production hard-fails on missing pairs. Click a cell to edit.
          </div>
        </div>
        <button
          type="button"
          disabled={seed.isPending}
          onClick={() => seed.mutate()}
          className="inline-flex h-9 items-center rounded-lg bg-[color:var(--bo-ink)] px-3.5 text-[12.5px] font-semibold text-white transition hover:bg-black disabled:cursor-not-allowed disabled:opacity-60"
        >
          {seed.isPending ? 'Seeding…' : 'Seed missing pairs from EUR cross'}
        </button>
      </div>

      {currencies.length === 0 ? (
        <div className="px-5 py-4 text-[12.5px] text-[color:var(--bo-mute)]">
          No display currencies configured yet — add them above first.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-left text-[12.5px]">
            <thead>
              <tr className="border-b border-[color:var(--bo-line)] bg-[color:var(--bo-bg)]">
                <th className="px-5 py-2 text-[11px] font-medium uppercase tracking-wide text-[color:var(--bo-mute)]">
                  Buy ↓ / Sell →
                </th>
                {currencies.map((s) => (
                  <th key={s} className="px-5 py-2 text-[11px] font-medium uppercase tracking-wide text-[color:var(--bo-mute)]">
                    {s}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-[color:var(--bo-line)]">
              {currencies.map((buy) => (
                <tr key={buy} className="hover:bg-[color:var(--bo-bg)]">
                  <td className="px-5 py-2 font-mono font-semibold text-[color:var(--bo-ink)]">{buy}</td>
                  {currencies.map((sell) => {
                    if (buy === sell) {
                      return (
                        <td key={sell} className="px-5 py-2 font-mono text-[color:var(--bo-mute)]">
                          1.00
                        </td>
                      );
                    }
                    const cell = cellMap.get(`${buy.toUpperCase()}_${sell.toUpperCase()}`);
                    const isEditing = editing && editing.buy === buy && editing.sell === sell;
                    if (isEditing) {
                      return (
                        <td key={sell} className="px-5 py-2">
                          <input
                            autoFocus
                            type="number"
                            step="0.0001"
                            min="0"
                            defaultValue={editing.value}
                            onBlur={(e) => commitCell(buy, sell, e.currentTarget.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') commitCell(buy, sell, e.currentTarget.value);
                              if (e.key === 'Escape') setEditing(null);
                            }}
                            className="w-24 rounded border border-[color:var(--bo-line)] px-2 py-1 font-mono text-[12.5px]"
                          />
                        </td>
                      );
                    }
                    return (
                      <td
                        key={sell}
                        className="cursor-pointer px-5 py-2 font-mono text-[color:var(--bo-ink)]"
                        onClick={() => setEditing({ buy, sell, value: cell?.rate.toString() ?? '' })}
                      >
                        {cell ? (
                          <span>
                            {cell.rate}
                            {cell.source === 'cross_from_eur' ? (
                              <span className="ms-1 text-[10px] text-[#9a3412]">×</span>
                            ) : null}
                          </span>
                        ) : (
                          <span className="text-[color:var(--bo-mute)]">— set</span>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {seedResult ? (
        <div className="border-t border-[color:var(--bo-line)] px-5 py-2 text-[12.5px] text-[color:var(--bo-mute)]">
          {seedResult}
        </div>
      ) : null}
    </section>
  );
}
